/**
 * LeadDetailPanel — right-docked drawer for a single lead.
 *
 * Mirrors DealDetailPanel's shape (desktop 380px flex-sibling dock, mobile
 * Radix bottom sheet with snap points) but for the lead surface. Kept as its
 * own component because DealDetailPanel is tightly bound to the Deal/inspector
 * shape — leads are unassigned contacts with a lead-status pill, not deals.
 *
 * Honours the global side-panel rule: NEVER a full-screen modal. No
 * `backdrop-blur` / `bg-black/60` — desktop is non-blocking, mobile uses a soft
 * translucent backdrop only.
 *
 * Primary action: "Convert to deal" creates a CRM deal from the lead and, on
 * success, refetches both pools so the lead drops out and the deal appears.
 */

import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, MapPin, User, Phone, Mail, Building2, ChevronDown, CheckCircle2, Loader2, ArrowRightLeft } from 'lucide-react';
import { clsx } from 'clsx';
import type { LeadWithSuburb } from '../../hooks/useLeadsWithSuburbs';
import { useCrm } from '../../contexts/CrmContext';
import { AdapterError } from '../../lib/crm/adapter';
import { LEAD_VIOLET, LEAD_TO_BOOK_VIOLET, leadDisplayName } from '../../lib/leadDisplay';
import { Row, SectionLabel, SNAP_HEIGHTS, type SnapPoint } from './_shared/panelPrimitives';

interface Props {
  lead: LeadWithSuburb | null;
  open: boolean;
  onClose: () => void;
  /** Called after a successful convert — parent should refetch deals + leads. */
  onConverted: () => void;
}

export function LeadDetailPanel({ lead, open, onClose, onConverted }: Props) {
  const [snap, setSnap] = useState<SnapPoint>('half');

  useEffect(() => {
    if (lead) setSnap('half');
  }, [lead?.lead.id]);

  if (!lead) return null;

  const accent = lead.leadStatus === 'lead_to_book' ? LEAD_TO_BOOK_VIOLET : LEAD_VIOLET;

  return (
    <>
      {/* DESKTOP: inline right-docked panel. Flex sibling — pushes layout. */}
      <aside
        className={clsx(
          'hidden h-full shrink-0 overflow-hidden border-l border-white/10 bg-neutral-950/95 transition-all duration-300 lg:block',
          open ? 'w-[380px]' : 'w-0',
        )}
      >
        {open && (
          <LeadPanelBody lead={lead} accent={accent} onClose={onClose} onConverted={onConverted} />
        )}
      </aside>

      {/* MOBILE: Radix Dialog as a bottom sheet — soft backdrop, snap points. */}
      <Dialog.Root open={open} onOpenChange={(o) => (!o ? onClose() : undefined)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-neutral-950/40 lg:hidden" />
          <Dialog.Content
            className="fixed inset-x-0 bottom-0 z-50 flex flex-col rounded-t-2xl border-t border-white/10 bg-neutral-950 shadow-2xl transition-[height] duration-300 lg:hidden"
            style={{ height: SNAP_HEIGHTS[snap] }}
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <div className="flex shrink-0 items-center justify-between border-b border-white/10 px-3 py-2">
              <div className="flex items-center gap-1">
                {(['peek', 'half', 'full'] as SnapPoint[]).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSnap(s)}
                    className={clsx(
                      'rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider',
                      snap === s ? 'bg-white/10 text-white' : 'text-neutral-500 hover:text-neutral-300',
                    )}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <Dialog.Close asChild>
                <button type="button" className="rounded-lg p-1 text-neutral-500 hover:bg-white/5">
                  <X className="h-4 w-4" />
                </button>
              </Dialog.Close>
            </div>
            <Dialog.Title className="sr-only">{leadDisplayName(lead.lead)}</Dialog.Title>
            <Dialog.Description className="sr-only">Lead detail</Dialog.Description>
            <div className="min-h-0 flex-1 overflow-y-auto">
              <LeadPanelBody lead={lead} accent={accent} onClose={onClose} onConverted={onConverted} hideChrome />
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}

interface BodyProps {
  lead: LeadWithSuburb;
  accent: string;
  onClose: () => void;
  onConverted: () => void;
  hideChrome?: boolean;
}

function LeadPanelBody({ lead, accent, onClose, onConverted, hideChrome }: BodyProps) {
  const { adapter } = useCrm();
  const { lead: c, regionMatch, leadStatus } = lead;
  const [converting, setConverting] = useState(false);
  const [converted, setConverted] = useState(false);
  const [convertError, setConvertError] = useState<string | null>(null);

  // Reset the convert state whenever a different lead loads into the panel.
  useEffect(() => {
    setConverting(false);
    setConverted(false);
    setConvertError(null);
  }, [c.id]);

  const name = leadDisplayName(lead.lead);
  const statusLabel = leadStatus === 'lead_to_book' ? 'Lead to book' : 'Lead';
  const v9Fields = Object.entries(c.customFields ?? {}).filter(([k]) => /^v9[_-]/i.test(k));

  const handleConvert = async () => {
    if (!adapter || !c.email || converting) return;
    setConverting(true);
    setConvertError(null);
    try {
      await adapter.createDeal({
        name: `${name} — ${c.address ?? 'no address'}`,
        contactEmail: c.email,
      });
      setConverted(true);
      onConverted();
    } catch (err) {
      const msg =
        err instanceof AdapterError ? err.message : err instanceof Error ? err.message : String(err);
      setConvertError(msg);
    } finally {
      setConverting(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      {!hideChrome && (
        <div className="flex shrink-0 items-center justify-between border-b border-white/10 px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rotate-45" style={{ backgroundColor: accent, boxShadow: `0 0 0 3px ${accent}22` }} />
            <div className="text-[10px] font-medium uppercase tracking-widest text-neutral-500">Lead</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-neutral-500 hover:bg-white/5 hover:text-neutral-300"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <h2 className="text-lg font-semibold leading-tight text-neutral-100">{name}</h2>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span
            className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
            style={{ backgroundColor: `${accent}22`, color: accent }}
          >
            {statusLabel}
          </span>
          {c.lifecycleStage && (
            <span className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[11px] capitalize text-neutral-400">
              {c.lifecycleStage}
            </span>
          )}
        </div>

        {/* Region card */}
        <div
          className="mt-4 rounded-xl border p-3"
          style={{ borderColor: `${accent}40`, backgroundColor: `${accent}0c` }}
        >
          <div className="text-[10px] font-medium uppercase tracking-widest" style={{ color: accent }}>
            Region match
          </div>
          {regionMatch?.zone ? (
            <>
              <div className="mt-1 text-sm font-medium text-neutral-100">
                {regionMatch.suburb ?? 'In service area'}
                {regionMatch.state ? `, ${regionMatch.state}` : ''}
              </div>
              <div className="mt-0.5 text-xs text-neutral-400">
                Zone {regionMatch.zone}
                {regionMatch.matchType !== 'none' && (
                  <span className="ml-1.5 text-neutral-600">· {regionMatch.matchType}</span>
                )}
              </div>
            </>
          ) : (
            <div className="mt-1 text-sm text-neutral-300">
              {c.address
                ? 'Address outside known service zones — remarketing pool.'
                : 'No address on this lead — remarketing pool.'}
            </div>
          )}
        </div>

        {/* Contact section */}
        <div className="mt-4 space-y-2">
          <SectionLabel>Contact</SectionLabel>
          {c.email ? (
            <Row icon={<Mail className="h-3.5 w-3.5" />} label={c.email} href={`mailto:${c.email}`} />
          ) : (
            <div className="text-xs text-neutral-500">No email — cannot convert to a deal.</div>
          )}
          {c.phone && <Row icon={<Phone className="h-3.5 w-3.5" />} label={c.phone} href={`tel:${c.phone}`} />}
          {c.company && <Row icon={<Building2 className="h-3.5 w-3.5" />} label={c.company} />}
          {(c.firstName || c.lastName) && (
            <Row icon={<User className="h-3.5 w-3.5" />} label={[c.firstName, c.lastName].filter(Boolean).join(' ')} />
          )}
          {c.address && (
            <Row
              icon={<MapPin className="h-3.5 w-3.5" />}
              label={c.address}
              href={`https://www.google.com/maps?q=${encodeURIComponent(c.address)}`}
              external
            />
          )}
        </div>

        {/* Velocity9 custom fields, if present */}
        {v9Fields.length > 0 && (
          <div className="mt-4 space-y-2">
            <SectionLabel>Velocity9 fields</SectionLabel>
            <dl className="space-y-1 rounded-xl border border-white/5 bg-black/20 p-2 text-[11px]">
              {v9Fields.map(([k, v]) => (
                <div key={k} className="grid grid-cols-[1fr_2fr] gap-2">
                  <dt className="truncate text-neutral-500">{k}</dt>
                  <dd className="truncate text-neutral-300">{String(v)}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        {/* All custom fields (collapsed) */}
        {Object.keys(c.customFields ?? {}).length > 0 && (
          <details className="group mt-6">
            <summary className="flex cursor-pointer list-none items-center gap-1 text-[11px] font-medium uppercase tracking-widest text-neutral-500 hover:text-neutral-300">
              <ChevronDown className="h-3 w-3 transition group-open:rotate-180" />
              Custom fields
            </summary>
            <dl className="mt-2 space-y-1 rounded-xl border border-white/5 bg-black/20 p-2 text-[11px]">
              {Object.entries(c.customFields).map(([k, v]) => (
                <div key={k} className="grid grid-cols-[1fr_2fr] gap-2">
                  <dt className="truncate text-neutral-500">{k}</dt>
                  <dd className="truncate text-neutral-300">{String(v)}</dd>
                </div>
              ))}
            </dl>
          </details>
        )}
      </div>

      {/* Primary action — convert to deal */}
      <div className="shrink-0 border-t border-white/10 p-3">
        {converted ? (
          <div className="flex items-center justify-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2.5 text-sm font-medium text-emerald-300">
            <CheckCircle2 className="h-4 w-4" />
            Converted to a deal
          </div>
        ) : (
          <>
            <button
              type="button"
              onClick={handleConvert}
              disabled={!c.email || converting}
              className={clsx(
                'flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold transition',
                !c.email
                  ? 'cursor-not-allowed border border-white/10 bg-white/[0.03] text-neutral-600'
                  : 'text-white hover:opacity-90 disabled:opacity-60',
              )}
              style={c.email ? { backgroundColor: LEAD_TO_BOOK_VIOLET } : undefined}
              title={c.email ? 'Create a CRM deal from this lead' : 'Lead has no email — cannot convert'}
            >
              {converting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRightLeft className="h-4 w-4" />}
              {converting ? 'Converting…' : 'Convert to deal'}
            </button>
            {convertError && <div className="mt-2 text-center text-[11px] text-red-300">{convertError}</div>}
          </>
        )}
      </div>
    </div>
  );
}
